/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of PositionPlugin
 */

import { IContext } from 'com/didichuxing/context/IContext';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { ITracker } from 'com/didichuxing/tracker/ITracker';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import { IEventReportor } from 'com/didichuxing/tracker/plugin/reportor/IEventReportor';
import { ITrackerConfiguration } from 'com/didichuxing/tracker/config/ITrackerConfiguration';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import SendStrategyChoice from 'com/didichuxing/tracker/h5/plugins/utils/SendStrategyChoice';
import OMGH5ReceivedEvent from 'com/didichuxing/tracker/h5/plugins/events/OMGH5ReceivedEvent';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
interface ILocationData {
    /** 纬度，浮点数，范围为-90~90，负数表示南纬 */
    latitude: number;
    /** 经度，浮点数，范围为-180~180，负数表示西经 */
    longitude: number;
    /** 速度，浮点数，单位m/s */
    speed: number;
    /** 位置的精确度 */
    accuracy: number;
}

export default class PositionPlugin extends AReportingStrategyPlugin {

    /**
     * @override
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_RECEIVED;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IEventReportor}
     */
    protected getEventReportor(event: ITrackedEvent): IEventReportor {
        return SendStrategyChoice.switchSendStrategy(this.getContext() as ITrackerContext, event);
    }

    /**
     * 获取地理位置。
     * @param {boolean} positionable
     */
    private fetchPosition(positionable: boolean): void {
        const appName = (this.getContext() as ITrackerContext).appName;
        // @ts-ignore
        if (appName && appName === 'WeChat' && window.wx && wx && wx.getLocation && typeof wx.getLocation === 'function') {
            // @ts-ignore
            wx.getLocation({
                    // 同步为浏览器返回标准
                    type: 'wgs84',
                    success: (locationData: ILocationData): void => {
                        this.getTracker().setLocation({
                            longitude: locationData.longitude,
                            latitude: locationData.latitude
                        });
                        this.report({
                            eventId: PluginUtils.EVENT_ID_RECEIVED,
                            attrs: {}
                        });
                    }
                });
        } else {
            // 乘客端桥接方法，避免WKWebView每次弹出权限框
            const didiLocationBridge = (window as any)['DiDiLocationBridge'];
            if (didiLocationBridge && didiLocationBridge.getLocation) {
                didiLocationBridge.getLocation((response: any) => {
                    this.setLocation(response.lng, response.lat);
                });
            } else if (positionable && navigator.geolocation) {
                // 标准浏览器 获取地理坐标。
                navigator.geolocation.getCurrentPosition((position: Position): void => {
                    this.setLocation(position.coords.longitude, position.coords.latitude);
                });
            }
        }

    }

    private setLocation(lng: number, lat: number) {
        this.getTracker().setLocation({
            longitude: lng || 0,
            latitude: lat || 0
        });
        this.report({
            eventId: PluginUtils.EVENT_ID_RECEIVED,
            attrs: {}
        });
    }

    /**
     * 对tracker.setConfig进行拦截。
     * @param {ITrackerConfiguration} config
     */
    private adviceSetConfigHandler(config: ITrackerConfiguration): void {
        if (typeof config.autoPosition === 'boolean'
            && config.autoPosition !== (this.getContext() as ITrackerContext).autoPosition) {
            this.fetchPosition(config.autoPosition); // 设置了自动上报地理坐标。
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    protected get initialzedContext(): IContext {
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        return {
            ...super.initialzedContext,
            autoPosition: typeof context.autoPosition === 'boolean' ? context.autoPosition : true
        } as IContext;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public install(): boolean {
        const tracker: ITracker = this.getTracker();
        this.fetchPosition((this.getContext() as ITrackerContext).autoPosition || false);
        const setConfigHandler: (config: ITrackerConfiguration) => void = tracker.setConfig;
        tracker.setConfig = (config: ITrackerConfiguration): void => {
            this.adviceSetConfigHandler(config);
            setConfigHandler.call(tracker, config);
        };
        return super.install();
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
        /* Nothing to execute */
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        /* Nothing to exit */
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        return new OMGH5ReceivedEvent(eventArgs.attrs, this.getContext());
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public uninstall(): boolean {
        this.adviceSetConfigHandler = (config: ITrackerConfiguration): void => {/* empty method */
        };
        return super.uninstall();
    }
}
